import * as z from 'zod';
import type { DesignSchemesBucketState, GlobalBucketState, LayersBucketState, PersistedBuckets } from './persistedBuckets';
import { LinesColorModeOptions } from '../ColorScheme';

const rangeSchema = z.tuple([z.number(), z.number()]);
const vectorSchema = z.object({ x: z.number(), y: z.number(), z: z.number() }).strict();

const paramControllerSchema = z
    .object({
        on: z.boolean().optional(),
        value: z.number(),
        minValue: z.number(),
        maxValue: z.number(),
    })
    .strict();

const peakControllerSchema = z
    .object({
        occurrences: z.array(z.string()),
        type: z.array(z.string()),
        values: z.array(z.string()),
    })
    .strict();

const contextStateSchema = z
    .object({
        showGround: z.boolean(),
        showBuildings: z.boolean(),
        showTrees: z.boolean(),
        showRoads: z.boolean(),
        showTargets: z.boolean(),
        showWindStations: z.boolean(),
        showCustomObstruction: z.boolean(),
        showProjectSketch: z.boolean(),
        showGrid: z.boolean(),
        showSiteBox: z.boolean(),
        showAnalysisVolume: z.boolean(),
        showGridCenter: z.boolean(),
        showAnalysisBorder: z.boolean(),
        showLegalBorder: z.boolean(),
        showBuildableBorder: z.boolean(),
        showGridAxes: z.boolean(),
    })
    .strict();

const cameraSchema = z
    .object({
        type: z.enum(['STANDARD', 'ORTHO', 'TOP', 'POV']),
        target: vectorSchema,
        position: vectorSchema,
    })
    .strict();

const ambientStateSchema = z
    .object({
        mode: z.string(),
        showStrokes: z.boolean(),
        wasdEnabled: z.boolean(),
        cameraWasdSpeed: z.number(),
        currentCamera: cameraSchema,
    })
    .strict();

const solarEnergyResourcesStateSchema = z
    .object({
        solarParams: z
            .object({
                diffuseRadiation: paramControllerSchema,
                cloudCoverage: paramControllerSchema,
                unitOfMeasure: z.number(),
            })
            .strict(),
        buildingPatterns: z
            .object({
                grossFloorSurface: paramControllerSchema,
                use: z.string(),
            })
            .strict(),
        energyDemand: z
            .object({
                yearlyRateMode: z.enum(['default', 'manual']),
                yearlyRateManual: z.number(),
                totalPeakMonth: z.string(),
                totalPeakRate: z.number(),
                heatingPeakMonth: z.string(),
                heatingPeakRate: z.number(),
            })
            .strict(),
        demandPeaks: z
            .object({
                heatingPeaks: peakControllerSchema,
                coolingPeaks: peakControllerSchema,
                appliancesPeaks: peakControllerSchema,
                totalPeaks: peakControllerSchema,
            })
            .strict(),
        photovoltaicPotentials: z
            .object({
                strategy: z.string(),
                panelEfficiency: paramControllerSchema,
                surfaceCoverage: paramControllerSchema,
                totalEnergySupply: z.number(),
                demand: z.number(),
                blockIdsByLayer: z.record(z.array(z.string())),
            })
            .strict(),
        solarHeatGains: z
            .object({
                strategy: z.string(),
                gValue: paramControllerSchema,
                transmittedEnergy: z.number(),
                heatingPeaksDemand: z.number(),
                blockIdsByLayer: z.record(z.array(z.string())),
            })
            .strict(),
        solarMatching: z
            .object({
                currentMatchingDemand: z.unknown().nullable(),
                matchingQueue: z.array(z.unknown()),
                isMatchingDemand: z.boolean(),
            })
            .strict(),
        yearlyDemand: z.number(),
    })
    .strict();

const performanceMetricsLayerStateSchema = z
    .object({
        thresholdLevel: z.union([z.number(), rangeSchema, z.null()]),
    })
    .strict();

const coordinateSystemSelectionSchema = z
    .object({
        mode: z.enum(['lv95', 'custom']),
        centerX: z.number(),
        centerY: z.number(),
        centerZ: z.number(),
        alpha: z.number(),
    })
    .strict();

const settingsStateSchema = z
    .object({
        colorScheme: z.string(),
        coloredItems: z.boolean().optional(),
        themeMode: z.enum(['light', 'dark']),
        lowPerformance: z.boolean(),
        coordinateSystem: coordinateSystemSelectionSchema,
    })
    .strict();

const dataStateSchema = z
    .object({
        isMixerMode: z.boolean(),
        mixerAndLogic: z.boolean(),
        mixerHigherLogic: z.boolean(),
        multimode: z.boolean(),
        showSelected: z.boolean(),
    })
    .strict();

const overlaysStateSchema = z
    .object({
        legendVisible: z.boolean(),
        fpsIndicatorVisible: z.boolean(),
        compassVisible: z.boolean(),
        absoluteMinMax: rangeSchema,
        remappedMinMax: rangeSchema,
        tmpMinMax: rangeSchema,
    })
    .strict();

const globalBucketStateSchema = z
    .object({
        activeGridId: z.string(),
        context: contextStateSchema,
        ambient: ambientStateSchema,
        solarEnergyResources: solarEnergyResourcesStateSchema,
        settings: settingsStateSchema,
        data: dataStateSchema,
        geodata: z
            .object({
                editGeodataEnabled: z.boolean(),
            })
            .strict(),
        overlays: overlaysStateSchema,
    })
    .strict();

const lineColorSchema = z
    .object({
        Lines_SUN_H: rangeSchema,
        Lines_VIEW_H: rangeSchema,
        Lines_NOISE_H: rangeSchema,
        Lines_WIND_H: rangeSchema,
        Lines_S: z.number(),
        Lines_L: z.number(),
        OBSTRUCTED_LINES_H: z.number(),
        OBSTRUCTED_LINES_S: z.number(),
        OBSTRUCTED_LINES_L: z.number(),
        refresh: z.boolean(),
        isBicolor: z.boolean().optional(),
    })
    .strict();

const layerStateSchema = z
    .object({
        geodataId: z.string(),
        pipeline: z
            .object({
                blocks: z.array(z.unknown()),
                executionCount: z.number(),
            })
            .strict(),
        pipelineGUIParams: z.record(z.record(z.unknown())),
        VECTORIAL_SCALAR_MODE: z.union([z.literal(0), z.literal(1)]),
        mixerElements: z.array(z.object({ layerId: z.string() }).passthrough()),
        filter: z
            .object({
                threshold: rangeSchema,
                indexFilter: z.boolean(),
                floorFilter: z.boolean(),
                closeToTerrain: rangeSchema,
                sliceX: rangeSchema,
                sliceY: rangeSchema,
                sliceZ: rangeSchema,
                unobstructed: z.boolean(),
                unobstructedScaled: z.boolean(),
                unobstructedModeOn: z.number(),
                unobstructedModes: z
                    .object({
                        VOXEL_CENTER: z.number(),
                        VOXEL_CORNERS: z.number(),
                    })
                    .strict(),
                filter_by_polygon: z.boolean(),
                polygonPositions: z.array(vectorSchema),
                only_in_buildable: z.boolean(),
                alone: z.boolean(),
                no_base: z.boolean(),
                index_tolerance: z.number(),
                index_subsort_mode: z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
                closeToBuilding: z.boolean(),
                closeToBuilding_dist: rangeSchema,
            })
            .strict(),
        projection: z
            .object({
                SEL_PROJECTION: z.number(),
                THETA: z.number(),
            })
            .strict(),
        element: z
            .object({
                normalized: z.boolean(),
                ELEMENT_INVERTED: z.boolean(),
                ELEMENT_SHOW_SELECTED: z.boolean(),
                SHOW_ZERO_VALUE_POINTS: z.boolean(),
                SENSOR_COLOR_MODE: z.union([z.literal(0), z.literal(1), z.literal(2)]),
                scaleFactor: z.number(),
                TRANSPARENCY: z.number(),
                SEL_VIS_ELEMENT: z.number(),
                ELEMENT_SPIN: z.number(),
                ELEMENT_CURRENT_DENSITY: z.number(),
                ELEMENT_1TO3: z.boolean(),
                ELEMENT_1EV3: z.boolean(),
                NO_STROKES: z.boolean(),
            })
            .strict(),
        simulationItems: z
            .object({
                x: z.number(),
                y: z.number(),
                z: z.number(),
                showRays: z.boolean(),
                raysQuantity: z.number(),
                raysEveryN: z.number(),
                linesLength: z.number(),
                freeTargetLinesLength: z.number(),
                showFreeLines: z.boolean(),
                showIntersectingLines: z.boolean(),
                heatmapX: z.number(),
                heatmapY: z.number(),
                linesColorModeOption: z.nativeEnum(LinesColorModeOptions),
                linesColorMode: lineColorSchema,
                showInts: z.boolean(),
                closestInts: z.boolean(),
                otherInts: z.boolean(),
                whichIntersections: z.number(),
                heatmapModeFv: z.boolean(),
            })
            .strict(),
        segments: z
            .object({
                frameMax: z.number(),
                frameSelected: z.number(),
                frameNames: z.array(z.string()),
                isLocal: z.boolean(),
                isGo: z.boolean().optional(),
                isAggregate: z.boolean(),
                framesList: z.array(z.number()),
                aggregateRange: rangeSchema,
                aggregateWrap: z.boolean(),
            })
            .strict(),
        data: z
            .object({
                activeResultId: z.number(),
            })
            .strict(),
        performanceMetrics: performanceMetricsLayerStateSchema.default({ thresholdLevel: null }),
        resultDomainOverrides: z
            .object({
                scalar: rangeSchema,
                vectorial: rangeSchema,
            })
            .strict()
            .nullable()
            .optional(),
    })
    .strict();

const layersBucketStateSchema = z
    .object({
        activeLayerID: z.string(),
        layerStates: z.record(layerStateSchema),
        failedLayers: z.record(z.string()),
    })
    .strict();

const floorBasedDesignStateSchema = z
    .object({
        active: z.boolean(),
        linkToMainLayer: z.boolean(),
        borderState: z
            .object({
                mode: z.number(),
                single: z.number().optional(),
                external: z.number().optional(),
                internal: z.number().optional(),
                projectSketchIndex: z.number().optional(),
                handDrawnBorder: z.array(vectorSchema).optional(),
                handDrawnExternalBorder: z.array(vectorSchema).optional(),
                handDrawnInternalBorder: z.array(vectorSchema).optional(),
            })
            .strict(),
        boxAlpha: paramControllerSchema,
        groundFloorHeight: paramControllerSchema,
        floorHeight: paramControllerSchema,
        thickness: paramControllerSchema,
        buildingHeight: paramControllerSchema,
        voxelFloors: z.boolean(),
        concave: z.boolean(),
        concaveAlpha: paramControllerSchema,
        facadeState: z
            .object({
                hide: z.boolean(),
                type: z.number(),
                componentTypes: z.array(z.number()),
                componentLayerIds: z.record(z.string(), z.string()),
                componentLink: z.boolean(),
                componentDirectionMode: z.number(),
                directionMode: z.number(),
                stacking: z.boolean(),
                horizontalLength: paramControllerSchema,
                horizontalFuseCoplanar: z.boolean(),
                verticalLength: paramControllerSchema,
                verticalCoverageMode: z.number(),
                verticalConnectCorners: z.boolean(),
                verticalClosed: z.boolean(),
                verticalHeight: paramControllerSchema,
                verticalCustomAngle: z.boolean(),
                verticalAngle: paramControllerSchema,
                pvOffset: paramControllerSchema,
                verticalHeightB: z.number(),
                balconyLength: paramControllerSchema,
                balconyConnectCorners: z.boolean(),
                balconyOnlyOnOpenings: z.boolean(),
                greenDepth: paramControllerSchema,
                shadingDepth: paramControllerSchema,
                shadingManual: z.boolean(),
                shadingConnectCorners: z.boolean(),
                shadingAutomaticRectangle: z.boolean(),
                shadingAutomaticRectangleReference: z.number(),
                shadingAutomaticLateralVerticalShade: z.boolean(),
            })
            .strict(),
    })
    .strict();

const boxDesignStateSchema = z
    .object({
        active: z.boolean(),
        transparent: z.boolean(),
        snapToVolume: z.boolean(),
        alpha: paramControllerSchema,
    })
    .strict();

const convexHullDesignStateSchema = z
    .object({
        active: z.boolean(),
        transparent: z.boolean(),
        concave: z.boolean(),
        concaveAlpha: paramControllerSchema,
    })
    .strict();

const orientedVolumeDesignStateSchema = z
    .object({
        active: z.boolean(),
        transparent: z.boolean(),
        concave: z.boolean(),
        concaveAlpha: paramControllerSchema,
        step: paramControllerSchema,
    })
    .strict();

const designSchemeStateSchema = z
    .object({
        schemeKey: z.enum(['FloorBased', 'ConvexHull', 'OrientedVolume', 'Box']),
        visibility: z.boolean(),
        linkedLayerIds: z.record(z.string(), z.string()).optional(),
        convexHull: convexHullDesignStateSchema.optional(),
        orientedVolume: orientedVolumeDesignStateSchema.optional(),
        box: boxDesignStateSchema.optional(),
        floorBased: floorBasedDesignStateSchema.optional(),
    })
    .strict();

const designSchemeSetStateSchema = z
    .object({
        grid: z.string(),
        id: z.string(),
        name: z.string(),
        type: z.number(),
        footprints: z.record(
            z
                .object({
                    id: z.string().optional(),
                    polygon: z.array(vectorSchema),
                    designSchemes: z.record(designSchemeStateSchema),
                })
                .strict()
        ),
        selectedFootprintId: z.string().nullable().optional(),
        visibility: z.boolean(),
        consistent: z.boolean(),
        outterSP: z.boolean(),
        innerSP: z.boolean(),
        shadow: z.boolean(),
        keepout: z.boolean(),
        closeToDesignSchemes: z.boolean(),
        refreshShadows: z.boolean(),
    })
    .strict();

const designSchemesBucketStateSchema = z
    .object({
        sets: z.record(designSchemeSetStateSchema),
        selectedSetId: z.string().optional(),
        defaults: z
            .object({
                convexHull: convexHullDesignStateSchema,
                orientedVolume: orientedVolumeDesignStateSchema,
                box: boxDesignStateSchema,
                floorBased: floorBasedDesignStateSchema,
            })
            .strict(),
    })
    .strict();

export const persistedBucketsSchema = z
    .object({
        global: globalBucketStateSchema,
        layers: layersBucketStateSchema,
        designSchemes: designSchemesBucketStateSchema,
    })
    .strict();

const formatIssues = (error: z.ZodError): string =>
    error.issues
        .slice(0, 3)
        .map((issue) => `${issue.path.join('.') || '<root>'}: ${issue.message}`)
        .join('; ');

type ValidationResult<T> = { success: true; data: T } | { success: false; error: string };

export const validateGlobalBucketState = (value: unknown): ValidationResult<GlobalBucketState> => {
    const parsed = globalBucketStateSchema.safeParse(value);
    if (parsed.success) return { success: true, data: parsed.data as unknown as GlobalBucketState };
    return { success: false, error: formatIssues(parsed.error) };
};

export const validateLayersBucketState = (value: unknown): ValidationResult<LayersBucketState> => {
    const parsed = layersBucketStateSchema.safeParse(value);
    if (parsed.success) return { success: true, data: parsed.data as unknown as LayersBucketState };
    return { success: false, error: formatIssues(parsed.error) };
};

export const validateDesignSchemesBucketState = (value: unknown): ValidationResult<DesignSchemesBucketState> => {
    const parsed = designSchemesBucketStateSchema.safeParse(value);
    if (parsed.success) return { success: true, data: parsed.data as unknown as DesignSchemesBucketState };
    return { success: false, error: formatIssues(parsed.error) };
};

export const validatePersistedBuckets = (value: unknown): ValidationResult<PersistedBuckets> => {
    const parsed = persistedBucketsSchema.safeParse(value);
    if (parsed.success) return { success: true, data: parsed.data as unknown as PersistedBuckets };
    return { success: false, error: formatIssues(parsed.error) };
};
